import { ClientReconnectControls } from '../components/ClientReconnectControls';
import { isPaymentTermsDays } from '../../shared/paymentTerms';
// Client detail per mockups/05-client-detail.html: breadcrumb with status
// pill + actions, projects list (phase dots + pills), invoice list with
// states, autosaving notes, and the contact/money rail. Inline editing
// everywhere.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { melbourneDate, melbourneToday } from '../../shared/dates';
import { displayCents, useMoneyMode } from '../lib/money';
import {
  CLIENT_STATUSES,
  LEAD_STATUS_LABELS,
  PHASE_LABELS,
  type ClientContactRow,
  type ClientRow,
  type ClientStatus,
  type InvoiceRow,
  type LeadStatus,
  type ProjectPhase,
  type ProjectRow,
} from '../../shared/types';
import {
  Dropdown,
  formatDate,
  formatDayMonth,
  formatInvoiceNumber,
  formatMoney,
  Group,
  GroupLink,
  InlineText,
  NotesBlock,
  Pill,
  Seg,
  TrashAction,
} from '../components/primitives';
import { NewProjectModal, NumberedTrashProjectRow } from './Projects';
import { NewInvoiceModal } from './Finance';
import { useIsMobile } from '../lib/useIsMobile';
import { ClientDetailMobile } from '../components/ClientDetailMobile';
import { contactDraftFrom, contactFields, type ContactDraft } from '../components/ContactSheet';

interface ClientProject extends ProjectRow {
  open_tasks: number;
}
interface ClientInvoice extends InvoiceRow {
  project_name: string | null;
  project_voided_at: number | null;
}
interface ClientNumberedTrashProject {
  id: string;
  name: string;
  project_number: number | null;
  deleted_at: number;
}
interface ClientLead {
  id: string;
  name: string;
  status: LeadStatus;
  company: string | null;
  contact_name: string | null;
  project_id: string | null;
  enquiry_date: string | null;
  closed_date: string | null;
  created_at: number;
}

const DASHBOARD_BASE = 'https://dashboard.sheahanstudios.com';

const STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Lead',
  active: 'Active',
  past: 'Past',
  archived: 'Archived',
};

function statusTone(s: ClientStatus): 'accent' | 'gold' | undefined {
  return s === 'active' ? 'accent' : s === 'lead' ? 'gold' : undefined;
}

const PHASE_DOT_CLASS: Partial<Record<ProjectPhase, string>> = {
  'lead': 'pd-lead',
  'pre-production': 'pd-pre',
  'shoot': 'pd-shoot',
  'post': 'pd-post',
  'clean_up': 'pd-cleanup',
  'on_hold': 'pd-hold',
};

/** "26 Jun" this year, "26 Jun 25" otherwise (client-detail project rows). */
function shortDate(iso: string | null, today: string): string {
  if (!iso) return '—';
  const base = formatDayMonth(iso);
  return iso.slice(0, 4) === today.slice(0, 4) ? base : `${base} ${iso.slice(2, 4)}`;
}

/** "Mar 2024" — client-since / first-project. */
function monthYearLong(iso: string | null): string {
  if (!iso) return '—';
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    Number(iso.slice(5, 7)) - 1
  ];
  return `${m} ${iso.slice(0, 4)}`;
}

function invoiceState(inv: InvoiceRow, today: string): { text: string; cls: string } {
  if (inv.status === 'paid')
    return { text: inv.paid_date ? `Paid ${formatDayMonth(inv.paid_date)}` : 'Paid', cls: ' paid' };
  if (inv.status === 'overdue' || (inv.status === 'sent' && inv.due_date && inv.due_date < today)) {
    const days = inv.due_date
      ? Math.max(1, Math.round((Date.parse(today) - Date.parse(inv.due_date)) / 86400000))
      : null;
    return { text: days ? `${days}d overdue` : 'Overdue', cls: ' overdue' };
  }
  if (inv.status === 'sent') return { text: 'Sent', cls: ' sent' };
  if (inv.status === 'void') return { text: 'Void', cls: '' };
  return { text: 'Draft', cls: '' };
}

export default function ClientDetail() {
  const isMobile = useIsMobile();
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<ClientRow | null>(null);
  const [projects, setProjects] = useState<ClientProject[]>([]);
  const [leads, setLeads] = useState<ClientLead[]>([]);
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [numberedTrashProjects, setNumberedTrashProjects] = useState<ClientNumberedTrashProject[]>([]);
  const [contacts, setContacts] = useState<ClientContactRow[]>([]);
  const [missing, setMissing] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [showNumberedTrash, setShowNumberedTrash] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [prefsLinkBusy, setPrefsLinkBusy] = useState(false);
  const [prefsLinkNote, setPrefsLinkNote] = useState<string | null>(null);
  const navigate = useNavigate();
  const today = melbourneToday();
  const mode = useMoneyMode();

  const listSeq = useRef(0);
  const fetchClient = useCallback(() => {
    const seq = ++listSeq.current;
    api<{
      client: ClientRow;
      projects: ClientProject[];
      leads: ClientLead[];
      invoices: ClientInvoice[];
      numbered_trash_projects: ClientNumberedTrashProject[];
      contacts: ClientContactRow[];
    }>(`/api/admin/clients/${id}`)
      .then((d) => {
        if (seq !== listSeq.current) return;
        setMissing(false);
        setClient(d.client);
        setProjects(d.projects);
        setLeads(d.leads ?? []);
        setInvoices(d.invoices);
        setNumberedTrashProjects(d.numbered_trash_projects ?? []);
        setContacts(d.contacts ?? []);
      })
      .catch(() => { if (seq === listSeq.current) setMissing(true); });
  }, [id]);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchClient();
  }, [fetchClient]);
  useEffect(() => {
    setClient(null);
    setMissing(false);
    setShowAllProjects(false);
    setShowAllLeads(false);
    fetchClient();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchClient, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchClient]);

  const patch = async (fields: Record<string, unknown>) => {
    if (!client) return;
    if ('payment_terms_days' in fields && fields.payment_terms_days !== null && !isPaymentTermsDays(fields.payment_terms_days)) {
      reportError(new Error('Payment terms must be an integer from 0 to 365, or blank for the default.'));
      return;
    }
    const prev = client;
    setClient({ ...client, ...fields } as ClientRow);
    try {
      await api(`/api/admin/clients/${client.id}`, { method: 'PATCH', json: fields });
      refresh();
    } catch (error) {
      setClient(prev);
      reportError(error, 'Could not save the client change.');
    }
  };

  // Client contact register (mockups-feature-builds §3a) — nested under the
  // client, same idiom as crew bookings under a project. Errors surface to
  // the calling form (ContactForm's try/catch), so these don't swallow them.
  const addContact = async (fields: Record<string, unknown>) => {
    if (!client) return;
    await api(`/api/admin/clients/${client.id}/contacts`, { method: 'POST', json: fields });
    refresh();
  };
  const patchContact = async (contactId: string, fields: Record<string, unknown>) => {
    if (!client) return;
    await api(`/api/admin/clients/${client.id}/contacts/${contactId}`, { method: 'PATCH', json: fields });
    refresh();
  };
  const deleteContact = async (contactId: string) => {
    if (!client) return;
    await api(`/api/admin/clients/${client.id}/contacts/${contactId}`, { method: 'DELETE' });
    refresh();
  };
  const scrollToContacts = () => {
    document.getElementById('contacts-group')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Notification preference centre (Phase 2.5) — generates the client's
  // public prefs token on demand, same idiom as InvoiceDetail's "Copy
  // public link".
  const copyPrefsLink = async () => {
    if (!client || prefsLinkBusy) return;
    setPrefsLinkBusy(true);
    setPrefsLinkNote(null);
    try {
      const result = await api<{ url: string }>(`/api/admin/clients/${client.id}/prefs-link`, { method: 'POST' });
      await navigator.clipboard.writeText(result.url);
      setPrefsLinkNote('Copied!');
    } catch (err) {
      setPrefsLinkNote(err instanceof Error ? err.message : 'Failed to create link.');
    } finally {
      setPrefsLinkBusy(false);
      setTimeout(() => setPrefsLinkNote(null), 4000);
    }
  };

  if (missing) {
    return (
      <div className="page-body">
        <div className="content">
          <div className="empty-note">Client not found.</div>
        </div>
      </div>
    );
  }
  if (!client) return <div className="page-body" />;

  const disp = (i: ClientInvoice) => displayCents(i.amount_cents, i.gst_cents, mode);
  const billed = invoices
    .filter(
      (i) => i.status === 'paid' || (i.project_voided_at === null && (i.status === 'sent' || i.status === 'overdue')),
    )
    .reduce((s, i) => s + disp(i), 0);
  const outstanding = invoices
    .filter((i) => i.project_voided_at === null && (i.status === 'sent' || i.status === 'overdue'))
    .reduce((s, i) => s + disp(i), 0);
  const overdueCount = invoices.filter(
    (i) =>
      i.project_voided_at === null &&
      (i.status === 'overdue' || (i.status === 'sent' && i.due_date && i.due_date < today)),
  ).length;
  const activeCount = projects.filter((p) => !['delivered', 'clean_up', 'closed'].includes(p.phase)).length;
  const firstDate =
    projects
      .map((p) => p.start_date ?? p.shoot_date ?? p.due_date ?? p.delivered_date)
      .filter((d): d is string => !!d)
      .sort()[0] ?? null;

  const subBits = [
    firstDate ? `Client since ${monthYearLong(firstDate)}` : null,
    `${projects.length} project${projects.length === 1 ? '' : 's'}`,
    billed > 0 ? `${formatMoney(billed)} billed all time` : null,
  ].filter(Boolean);

  const visibleProjects = showAllProjects ? projects : projects.slice(0, 4);
  const visibleLeads = showAllLeads ? leads : leads.slice(0, 4);
  const visibleInvoices = invoices.slice(0, 6);
  const defaultContact = contacts.find((c) => c.is_default === 1) ?? null;

  if (isMobile) return <>
    <ClientDetailMobile key={client.id} client={client} projects={projects} invoices={invoices} leads={leads} contacts={contacts}
      billed={billed} outstanding={outstanding} overdueCount={overdueCount} activeCount={activeCount} firstProject={monthYearLong(firstDate)}
      today={today} displayInvoice={disp} invoiceState={invoiceState} shortDate={shortDate}
      patch={patch} addContact={addContact} patchContact={patchContact} deleteContact={deleteContact}
      onNewProject={() => setShowNewProject(true)} onNewInvoice={() => setShowNewInvoice(true)}
      onCopyPrefs={copyPrefsLink} prefsLinkBusy={prefsLinkBusy} prefsLinkNote={prefsLinkNote}
      onTrash={async () => { await api(`/api/admin/clients/${client.id}`, { method: 'DELETE' }); navigate('/clients'); }} />
    {showNewProject && <NewProjectModal defaultClientId={client.id} onClose={() => setShowNewProject(false)} onCreated={refresh} />}
    {showNewInvoice && <NewInvoiceModal defaultClientId={client.id} onClose={() => setShowNewInvoice(false)} onCreated={refresh} />}
  </>;

  return (
    <>
      <header className="detail-header">
        <Link className="crumb" to="/clients">
          Clients
        </Link>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">{client.name}</span>
        <span style={{ marginLeft: 6 }}>
          <Pill tone={statusTone(client.status)}>{STATUS_LABELS[client.status]}</Pill>
        </span>
        <div className="detail-actions">
          <button className="btn" onClick={() => setShowNewProject(true)}>
            + Project
          </button>
          {client.dashboard_slug && (
            <a
              className="btn"
              href={`${DASHBOARD_BASE}/${client.dashboard_slug}`}
              target="_blank"
              rel="noreferrer"
            >
              Dashboard ↗
            </a>
          )}
          <button className="btn primary" onClick={() => setShowNewInvoice(true)}>
            + Invoice
          </button>
        </div>
      </header>

      <div className="page-body">
        <div className="detail-grid">
          <div className="detail-main">
            <h1 className="detail-name">{client.name}</h1>
            <div className="detail-sub">{subBits.join(' · ')}</div>

            <Group
              title="Projects"
              count={projects.length}
              link={
                <a className="group-link" style={{ cursor: 'pointer' }} onClick={() => setShowNewProject(true)}>
                  + New project
                </a>
              }
            >
              {visibleProjects.map((p) => {
                const delivered = p.phase === 'delivered' || p.phase === 'clean_up' || p.phase === 'closed';
                return (
                  <Link className="row" key={p.id} to={`/projects/${p.id}`}>
                    <span
                      className={`phase-dot ${PHASE_DOT_CLASS[p.phase] ?? ''}`}
                      style={delivered && p.phase !== 'clean_up' ? { background: 'var(--ok)' } : undefined}
                    />
                    <span className="row-title" title={p.name}>{p.name}</span>
                    {!delivered && p.open_tasks > 0 && (
                      <span
                        className="row-context"
                        title={`${p.open_tasks} open task${p.open_tasks === 1 ? '' : 's'}`}
                      >
                        {p.open_tasks} open task{p.open_tasks === 1 ? '' : 's'}
                      </span>
                    )}
                    <span className="row-spacer" />
                    <Pill tone={p.phase === 'post' ? 'accent' : p.phase === 'shoot' ? 'gold' : p.phase === 'clean_up' ? 'cleanup' : undefined}>
                      {PHASE_LABELS[p.phase]}
                    </Pill>
                    <span className={`row-meta col-due${delivered ? ' dim' : ''}`}>
                      {shortDate(delivered ? p.delivered_date : p.due_date, today)}
                    </span>
                  </Link>
                );
              })}
              {projects.length > 4 && !showAllProjects && (
                <a
                  className="group-link"
                  style={{ display: 'block', padding: '10px 0 0', cursor: 'pointer' }}
                  onClick={() => setShowAllProjects(true)}
                >
                  Show all {projects.length} →
                </a>
              )}
              {projects.length === 0 && <div className="empty-note">No projects yet.</div>}
              {numberedTrashProjects.length > 0 && !showNumberedTrash && (
                <button className="show-more" onClick={() => setShowNumberedTrash(true)}>
                  Show {numberedTrashProjects.length} numbered trash record{numberedTrashProjects.length === 1 ? '' : 's'} →
                </button>
              )}
              {showNumberedTrash && numberedTrashProjects.map((p) => (
                <NumberedTrashProjectRow key={p.id} p={{ ...p, client_name: '' }} />
              ))}
            </Group>

            {leads.length > 0 && (
              <Group title="Leads" count={leads.length}>
                {visibleLeads.map((lead) => (
                  <Link className="row" key={lead.id} to={`/leads/${lead.id}`}>
                    <span className="row-title" title={lead.name}>{lead.name}</span>
                    {lead.company && lead.company !== client.name && (
                      <span className="row-context" title={lead.company}>{lead.company}</span>
                    )}
                    <span className="row-spacer" />
                    <Pill tone={lead.status === 'locked_in' ? 'ok' : undefined}>{LEAD_STATUS_LABELS[lead.status]}</Pill>
                    <span className="row-meta col-due">
                      {shortDate(lead.closed_date ?? lead.enquiry_date, today)}
                    </span>
                  </Link>
                ))}
                {leads.length > 4 && !showAllLeads && (
                  <a
                    className="group-link"
                    style={{ display: 'block', padding: '10px 0 0', cursor: 'pointer' }}
                    onClick={() => setShowAllLeads(true)}
                  >
                    Show all {leads.length} →
                  </a>
                )}
              </Group>
            )}

            <Group
              title="Invoices"
              count={invoices.length}
              link={<GroupLink to="/finance">All in finance →</GroupLink>}
            >
              {visibleInvoices.map((inv) => {
                const state = invoiceState(inv, today);
                return (
                  <Link className="row" key={inv.id} to={`/finance/invoices/${inv.id}`}>
                    <span className="row-meta">{formatInvoiceNumber(inv.invoice_number)}</span>
                    <span className="row-title" title={inv.project_name ?? '—'}>{inv.project_name ?? '—'}</span>
                    <span className="row-spacer" />
                    <span className={`inv-state${state.cls}`}>{state.text}</span>
                    <span className="row-meta gold col-amt">{formatMoney(disp(inv))}</span>
                  </Link>
                );
              })}
              {invoices.length === 0 && <div className="empty-note">No invoices yet.</div>}
            </Group>

            <div id="contacts-group">
              <Group
                title="Contacts"
                count={contacts.length}
                link={
                  !showAddContact && (
                    <a className="group-link" style={{ cursor: 'pointer' }} onClick={() => setShowAddContact(true)}>
                      + Add contact
                    </a>
                  )
                }
              >
                {contacts.map((c) => (
                  <ContactEntryRow
                    key={c.id}
                    contact={c}
                    defaultDeleteBlocked={c.is_default === 1 && contacts.length > 1}
                    onSave={(fields) => patchContact(c.id, fields)}
                    onDelete={() => deleteContact(c.id)}
                  />
                ))}
                {contacts.length === 0 && !showAddContact && <div className="empty-note">No contacts yet.</div>}
                {showAddContact && (
                  <ContactForm
                    showDefaultToggle
                    submitLabel="Add"
                    onSubmit={async (fields) => {
                      await addContact(fields);
                      setShowAddContact(false);
                    }}
                    onCancel={() => setShowAddContact(false)}
                  />
                )}
              </Group>
            </div>

            <NotesBlock notes={client.notes} onSave={(notes) => patch({ notes })} />
          </div>

          <div className="detail-rail">
            <div className="field">
              <div className="field-label">Status</div>
              <div className="dropdown-anchor">
                <span className="select-pill" onClick={() => setStatusOpen((o) => !o)}>
                  <Pill tone={statusTone(client.status)}>{STATUS_LABELS[client.status]}</Pill>
                  <span className="caret">▾</span>
                </span>
                {statusOpen && (
                  <Dropdown
                    items={CLIENT_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
                    value={client.status}
                    onSelect={(status) => patch({ status })}
                    onClose={() => setStatusOpen(false)}
                  />
                )}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Default contact</div>
              <a
                className="field-value"
                style={{ color: 'var(--accent)', cursor: 'pointer' }}
                onClick={scrollToContacts}
              >
                {defaultContact
                  ? `${defaultContact.name}${defaultContact.role_label ? ` · ${defaultContact.role_label}` : ''}`
                  : 'No contacts yet'}{' '}
                →
              </a>
            </div>
            <div className="field">
              <div className="field-label">Website</div>
              <InlineText
                value={client.website}
                mono
                valueClass="mono"
                display={
                  client.website ? <span style={{ color: 'var(--accent)' }}>{client.website}</span> : undefined
                }
                onSave={(website) => patch({ website })}
              />
            </div>
            <div className="field">
              <div className="field-label">Address</div>
              <InlineText value={client.address} onSave={(address) => patch({ address })} />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="client-payment-terms">Payment terms (days)</label>
              <input id="client-payment-terms" className="f-input mono" type="number" min={0} max={365} step={1}
                key={`${client.id}:${client.payment_terms_days}`}
                defaultValue={client.payment_terms_days ?? ''} placeholder="Default"
                onBlur={(e) => {
                  if (!e.currentTarget.reportValidity()) return;
                  const days = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
                  if (days !== client.payment_terms_days) void patch({ payment_terms_days: days });
                }} />
              <span className="f-help">Used for the due date on new invoices</span>
            </div>
            <div className="field">
              <div className="field-label">Client dashboard</div>
              <InlineText
                value={client.dashboard_slug}
                mono
                valueClass="mono"
                placeholder="slug"
                display={
                  client.dashboard_slug ? (
                    <span style={{ color: 'var(--accent)' }}>/{client.dashboard_slug}</span>
                  ) : undefined
                }
                onSave={(dashboard_slug) => patch({ dashboard_slug })}
              />
            </div>
            <div className="field">
              <div className="field-label">Reconnect preferences</div>
              <ClientReconnectControls id={client.id} name={client.name} blocked={!!client.do_not_contact} onSaved={refresh}/>
            </div>
            <div className="field">
              <div className="field-label">Email preferences</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <Pill tone={client.notify_invoices ? 'accent' : undefined}>
                  Invoices {client.notify_invoices ? 'on' : 'off'}
                </Pill>
                <Pill tone={client.notify_nudges ? 'accent' : undefined}>
                  Nudges {client.notify_nudges ? 'on' : 'off'}
                </Pill>
                <Pill tone={client.notify_milestones ? 'accent' : undefined}>
                  Milestones {client.notify_milestones ? 'on' : 'off'}
                </Pill>
              </div>
              <button className="btn block" onClick={copyPrefsLink} disabled={prefsLinkBusy}>
                {prefsLinkBusy ? 'Creating…' : prefsLinkNote ?? 'Copy preferences link'}
              </button>
            </div>
            <div className="field">
              <div className="field-label">Billed all time</div>
              <div className="field-value gold" style={{ fontSize: 15 }}>
                {formatMoney(billed)}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Outstanding</div>
              <div className="field-value gold">
                {formatMoney(outstanding)}
                {overdueCount > 0 && (
                  <span style={{ color: 'var(--danger)', fontSize: 10, fontFamily: 'var(--font-mono)' }}>
                    {' '}
                    · {overdueCount} OVERDUE
                  </span>
                )}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Projects</div>
              <div className="field-value mono">
                {projects.length} total · {activeCount} active
              </div>
            </div>
            <div className="field">
              <div className="field-label">First project</div>
              <div className="field-value mono">{monthYearLong(firstDate)}</div>
            </div>
            <div className="meta-stamp">
              CREATED {formatDate(melbourneDate(client.created_at)).toUpperCase()}
              <br />
              UPDATED {formatDate(melbourneDate(client.updated_at)).toUpperCase()}
            </div>
            <TrashAction
              label="Move client to trash"
              onConfirm={async () => {
                try {
                  await api(`/api/admin/clients/${client.id}`, { method: 'DELETE' });
                  navigate('/clients');
                } catch (error) {
                  // 409 when projects/invoices/leads are still attached — say so
                  // instead of silently doing nothing.
                  reportError(error, 'Could not move the client to trash.');
                }
              }}
            />
          </div>
        </div>
      </div>

      {showNewProject && (
        <NewProjectModal
          defaultClientId={client.id}
          onClose={() => setShowNewProject(false)}
          onCreated={refresh}
        />
      )}
      {showNewInvoice && (
        <NewInvoiceModal
          defaultClientId={client.id}
          onClose={() => setShowNewInvoice(false)}
          onCreated={refresh}
        />
      )}
    </>
  );
}

// ── Contacts group (mockups-feature-builds §3a) ──────────────────────────
// "+ Add contact" and a row's "Edit" share this inline form — the same
// idiom as ProjectDetail's "+ Add crew" (AddCrewBooking), not a modal.

function ContactForm({
  initial,
  showDefaultToggle,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: ClientContactRow;
  /** Hidden when editing the row that's already the default — a default
      can't be unset via this form, only replaced by picking a new one
      (routes/clients.ts rejects `is_default: false` on the live default). */
  showDefaultToggle: boolean;
  submitLabel: string;
  onSubmit: (fields: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ContactDraft>(contactDraftFrom(initial));
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!draft.name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const fields = contactFields(draft, makeDefault);
      await onSubmit(fields);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save contact.');
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Name</span>
          <input
            className="f-input"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            autoFocus
          />
        </div>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Role</span>
          <input
            className="f-input"
            value={draft.role_label}
            onChange={(e) => setDraft((d) => ({ ...d, role_label: e.target.value }))}
            placeholder="Marketing lead…"
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Email</span>
          <input
            className="f-input mono"
            value={draft.email}
            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            placeholder="name@company.com.au"
          />
        </div>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Phone</span>
          <input
            className="f-input mono"
            value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            placeholder="04…"
          />
        </div>
      </div>
      {showDefaultToggle && (
        <div className="f-field">
          <span className="f-label">Default</span>
          <Seg
            options={[
              { value: 'no', label: 'No' },
              { value: 'yes', label: 'Make default' },
            ]}
            value={makeDefault ? 'yes' : 'no'}
            onChange={(v) => setMakeDefault(v === 'yes')}
          />
        </div>
      )}
      {error && <p className="error-text">{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" onClick={submit} disabled={!draft.name.trim() || busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button className="btn" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ContactEntryRow({
  contact,
  defaultDeleteBlocked,
  onSave,
  onDelete,
}: {
  contact: ClientContactRow;
  defaultDeleteBlocked: boolean;
  onSave: (fields: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const isDefault = contact.is_default === 1;
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="contact-entry">
        <ContactForm
          initial={contact}
          showDefaultToggle={!isDefault}
          submitLabel="Save"
          onSubmit={async (fields) => {
            await onSave(fields);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="contact-entry">
      <div className="contact-top">
        <span className="contact-name">{contact.name}</span>
        {isDefault && <Pill tone="accent">Default</Pill>}
        <span className="row-spacer" />
        <a className="group-link" style={{ cursor: 'pointer' }} onClick={() => setEditing(true)}>
          Edit
        </a>
        <TrashAction
          label="Delete"
          onConfirm={onDelete}
          disabled={defaultDeleteBlocked}
          title={defaultDeleteBlocked ? 'Make another contact the default first' : undefined}
        />
      </div>
      <div className="contact-sub">
        <span className="contact-role">{contact.role_label || '—'}</span>
        <span className="contact-email">{contact.email || '—'}</span>
        <span className="contact-phone">{contact.phone || '—'}</span>
      </div>
    </div>
  );
}
